import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { sendGeminiMessage, isGeminiConfigured, GEMINI_MODELS, IMAGE_OPTIONS } from '../services/geminiService';
import { saveConversation, getMessages, createSession } from '../services/chatApiService';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

// 세션 ID 생성
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// 세션스토리지에서 세션 데이터 로드
const loadSessionData = () => {
  try {
    const saved = sessionStorage.getItem('chatSession');
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        sessionId: parsed.sessionId || generateSessionId(),
        backendSessionId: parsed.backendSessionId || null, // 백엔드에서 받은 실제 세션 ID
        messages: parsed.messages || [],
        title: parsed.title || null,
      };
    }
    return {
      sessionId: generateSessionId(),
      backendSessionId: null,
      messages: [],
      title: null,
    };
  } catch (e) {
    console.error('Failed to load chat session:', e);
    return {
      sessionId: generateSessionId(),
      backendSessionId: null,
      messages: [],
      title: null,
    };
  }
};

// 로컬스토리지에서 핀 상태 로드
const loadPinnedState = () => {
  try {
    const saved = localStorage.getItem('chatPinned');
    return saved ? JSON.parse(saved) : false;
  } catch (e) {
    console.error('Failed to load pinned state:', e);
    return false;
  }
};

// 버튼 위치 기본값 ('left' | 'right')
const DEFAULT_BUTTON_POSITION = {
  mobile: 'right',
  desktop: 'right',
};

// 로컬스토리지에서 버튼 위치 로드
const loadButtonPosition = () => {
  try {
    const saved = localStorage.getItem('chatButtonPosition');
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        mobile: parsed.mobile === 'left' ? 'left' : 'right',
        desktop: parsed.desktop === 'left' ? 'left' : 'right',
      };
    }
    return DEFAULT_BUTTON_POSITION;
  } catch (e) {
    console.error('Failed to load button position:', e);
    return DEFAULT_BUTTON_POSITION;
  }
};

export const ChatProvider = ({ children }) => {
  // 채팅창 열림 상태 (메모리만, 새로고침 시 닫힘)
  const [isOpen, setIsOpen] = useState(false);

  // 핀 고정 상태 (localStorage 저장)
  const [isPinned, setIsPinned] = useState(loadPinnedState);

  // 세션 데이터 로드
  const [sessionData, setSessionData] = useState(loadSessionData);

  // 입력 중인 메시지
  const [inputMessage, setInputMessage] = useState('');

  // 로딩 상태
  const [isLoading, setIsLoading] = useState(false);

  // 채팅 패널 너비 (기본값 360px)
  const [panelWidth, setPanelWidth] = useState(360);

  // 이미지 생성 모드 (체크박스로 토글)
  const [imageMode, setImageMode] = useState(false);

  // 웹 검색 모드 (체크박스로 토글, 텍스트 모드에서만 사용)
  const [webSearchMode, setWebSearchMode] = useState(false);

  // 이미지 생성 옵션
  const [imageOptions, setImageOptions] = useState({
    aspectRatio: IMAGE_OPTIONS.DEFAULTS.aspectRatio,
    imageSize: IMAGE_OPTIONS.DEFAULTS.imageSize,
  });

  // 버튼 위치 (right 값, 모바일/데스크톱 별도)
  const [buttonPosition, setButtonPositionState] = useState(loadButtonPosition);

  // 현재 사용 중인 모델 표시
  const currentModel = imageMode ? GEMINI_MODELS.IMAGE : GEMINI_MODELS.TEXT;

  // 편의를 위한 messages getter
  const messages = sessionData.messages;

  // 세션 데이터 변경 시 sessionStorage 저장
  useEffect(() => {
    try {
      sessionStorage.setItem('chatSession', JSON.stringify(sessionData));
    } catch (e) {
      console.error('Failed to save chat session:', e);
    }
  }, [sessionData]);

  // 핀 상태 변경 시 localStorage 저장
  useEffect(() => {
    try {
      localStorage.setItem('chatPinned', JSON.stringify(isPinned));
    } catch (e) {
      console.error('Failed to save pinned state:', e);
    }
  }, [isPinned]);

  // 버튼 위치 변경 시 localStorage 저장
  useEffect(() => {
    try {
      localStorage.setItem('chatButtonPosition', JSON.stringify(buttonPosition));
    } catch (e) {
      console.error('Failed to save button position:', e);
    }
  }, [buttonPosition]);

  // 채팅창 토글
  const toggleChat = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // 채팅창 열기
  const openChat = useCallback(() => {
    setIsOpen(true);
  }, []);

  // 채팅창 닫기
  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  // 핀 토글
  const togglePin = useCallback(() => {
    setIsPinned((prev) => !prev);
  }, []);

  // 이미지 모드 토글
  const toggleImageMode = useCallback(() => {
    setImageMode((prev) => !prev);
  }, []);

  // 웹 검색 모드 토글
  const toggleWebSearch = useCallback(() => {
    setWebSearchMode((prev) => !prev);
  }, []);

  // 이미지 옵션 업데이트
  const updateImageOptions = useCallback((key, value) => {
    setImageOptions((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // 버튼 위치 업데이트 ('left' | 'right')
  const setButtonPosition = useCallback((isMobile, position) => {
    const validPosition = position === 'left' ? 'left' : 'right';
    setButtonPositionState((prev) => ({
      ...prev,
      [isMobile ? 'mobile' : 'desktop']: validPosition,
    }));
  }, []);

  // 버튼 위치 토글 (left ↔ right)
  const toggleButtonPosition = useCallback((isMobile) => {
    setButtonPositionState((prev) => {
      const key = isMobile ? 'mobile' : 'desktop';
      return {
        ...prev,
        [key]: prev[key] === 'left' ? 'right' : 'left',
      };
    });
  }, []);

  // 버튼 위치 초기화
  const resetButtonPosition = useCallback((isMobile) => {
    setButtonPositionState((prev) => ({
      ...prev,
      [isMobile ? 'mobile' : 'desktop']: DEFAULT_BUTTON_POSITION[isMobile ? 'mobile' : 'desktop'],
    }));
  }, []);

  // 백엔드에 대화 저장 (비동기, 실패해도 UI에 영향 없음)
  const saveToBackend = useCallback(async (backendSessionId, userMessage, assistantMessage) => {
    if (!backendSessionId) return;

    try {
      await saveConversation(backendSessionId, userMessage, assistantMessage);
      console.log('[Chat] 백엔드에 대화 저장 완료');
    } catch (error) {
      console.warn('[Chat] 백엔드 저장 실패 (로컬에는 저장됨):', error.message);
    }
  }, []);

  // 백엔드 세션 생성
  const createBackendSession = useCallback(async (title) => {
    try {
      const result = await createSession(title);
      if (result?.data?.id) {
        return result.data.id;
      }
      return null;
    } catch (error) {
      console.warn('[Chat] 백엔드 세션 생성 실패:', error.message);
      return null;
    }
  }, []);

  // 메시지 전송
  const sendMessage = useCallback(
    async (content) => {
      if (!content.trim()) return;

      const userMessage = {
        id: `msg_${Date.now()}_user`,
        role: 'user',
        content: content.trim(),
        images: [],
        timestamp: new Date().toISOString(),
      };

      // 세션 제목 설정 (첫 메시지면)
      const newTitle = sessionData.title || content.trim().substring(0, 50);

      // 사용자 메시지 추가
      setSessionData((prev) => ({
        ...prev,
        messages: [...prev.messages, userMessage],
        title: newTitle,
      }));

      setInputMessage('');
      setIsLoading(true);

      try {
        // Gemini API가 설정되어 있는지 확인
        if (!isGeminiConfigured()) {
          throw new Error('Gemini API가 설정되지 않았습니다. 환경변수를 확인해주세요.');
        }

        // Gemini API 호출 (프론트엔드에서 직접)
        // 이미지 모드가 아닐 때만 웹 검색 사용
        const response = await sendGeminiMessage(content, sessionData.messages, imageMode, imageOptions, !imageMode && webSearchMode);

        const assistantMessage = {
          id: `msg_${Date.now()}_assistant`,
          role: 'assistant',
          content: response.content,
          images: response.images || [],
          sources: response.groundingMetadata || null,
          timestamp: new Date().toISOString(),
          model: currentModel,
        };

        // 로컬 상태 업데이트
        setSessionData((prev) => ({
          ...prev,
          messages: [...prev.messages, assistantMessage],
        }));

        // 백엔드에 저장 (비동기, 실패해도 무시)
        // 첫 메시지인 경우 백엔드 세션 생성
        let backendId = sessionData.backendSessionId;
        if (!backendId && sessionData.messages.length === 0) {
          backendId = await createBackendSession(newTitle);
          if (backendId) {
            setSessionData((prev) => ({
              ...prev,
              backendSessionId: backendId,
            }));
          }
        }

        // 백엔드에 대화 저장
        saveToBackend(backendId, userMessage, assistantMessage);
      } catch (error) {
        console.error('Failed to send message:', error);

        const errorMessage = {
          id: `msg_${Date.now()}_error`,
          role: 'assistant',
          content: `메시지 전송에 실패했습니다: ${error.message}`,
          images: [],
          timestamp: new Date().toISOString(),
          isError: true,
        };

        setSessionData((prev) => ({
          ...prev,
          messages: [...prev.messages, errorMessage],
        }));
      } finally {
        setIsLoading(false);
      }
    },
    [sessionData, imageMode, imageOptions, webSearchMode, currentModel, createBackendSession, saveToBackend]
  );

  // 백엔드에서 메시지 불러오기
  const loadMessagesFromBackend = useCallback(async (backendSessionId) => {
    if (!backendSessionId) return;

    try {
      const result = await getMessages(backendSessionId);
      if (result?.data?.messages) {
        setSessionData((prev) => ({
          ...prev,
          messages: result.data.messages.map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            images: [], // 백엔드에서 이미지는 별도 저장하지 않음
            timestamp: msg.createdAt,
            model: msg.model,
          })),
        }));
      }
    } catch (error) {
      console.warn('[Chat] 백엔드에서 메시지 불러오기 실패:', error.message);
    }
  }, []);

  // 메시지에 이미지 추가 (AI 응답에서 이미지가 올 때)
  const addImageToMessage = useCallback((messageId, image) => {
    setSessionData((prev) => ({
      ...prev,
      messages: prev.messages.map((msg) => {
        if (msg.id === messageId) {
          return {
            ...msg,
            images: [...msg.images, image],
          };
        }
        return msg;
      }),
    }));
  }, []);

  // 채팅 기록 삭제 (현재 세션)
  const clearChat = useCallback(() => {
    setSessionData({
      sessionId: generateSessionId(),
      backendSessionId: null,
      messages: [],
      title: null,
    });
    sessionStorage.removeItem('chatSession');
  }, []);

  // 새 채팅 시작
  const startNewChat = useCallback(() => {
    setSessionData({
      sessionId: generateSessionId(),
      backendSessionId: null,
      messages: [],
      title: null,
    });
  }, []);

  const value = {
    // 상태
    isOpen,
    isPinned,
    messages,
    inputMessage,
    isLoading,
    panelWidth,
    imageMode,
    imageOptions,
    webSearchMode,
    currentModel,
    sessionId: sessionData.sessionId,
    backendSessionId: sessionData.backendSessionId,
    sessionTitle: sessionData.title,
    buttonPosition,

    // 상수
    IMAGE_OPTIONS,
    DEFAULT_BUTTON_POSITION,

    // 메서드
    toggleChat,
    openChat,
    closeChat,
    togglePin,
    setIsPinned,
    sendMessage,
    setInputMessage,
    addImageToMessage,
    clearChat,
    setPanelWidth,
    toggleImageMode,
    setImageMode,
    toggleWebSearch,
    setWebSearchMode,
    updateImageOptions,
    startNewChat,
    loadMessagesFromBackend,
    setButtonPosition,
    toggleButtonPosition,
    resetButtonPosition,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export default ChatContext;
